import { useState, useMemo } from 'react';
import useOnTaskIdentity from './useOnTaskIdentity';
import { getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * useArtifacts Hook
 * 
 * Manages inputs and deliverables state and logic for TaskNode.
 * Handles upstream task traversal and artifact operations.
 */
export const useArtifacts = ({
  task,
  allTasks,
  onDeliverablesChange,
  onInputsChange,
}) => {
  // Deliverables: { id, name, link (optional) }
  const [deliverables, setDeliverables] = useState(task.deliverables || []);
  const [showAddDeliverable, setShowAddDeliverable] = useState(false);
  const [newDeliverableName, setNewDeliverableName] = useState('');
  
  // Inputs: { id, name, sourceType: 'mission' | 'external', sourceMissionId?, sourceDeliverableId? }
  const [inputs, setInputs] = useState(task.inputs || []);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newInputName, setNewInputName] = useState('');
  const [newInputSourceType, setNewInputSourceType] = useState('external');
  const [newInputSourceMissionId, setNewInputSourceMissionId] = useState('');
  const [newInputSourceDeliverableId, setNewInputSourceDeliverableId] = useState('');

  useOnTaskIdentity(task.id, () => {
    setDeliverables(task.deliverables || []);
    setInputs(task.inputs || []);
    setShowAddDeliverable(false);
    setShowAddInput(false);
    setNewDeliverableName('');
    setNewInputName('');
    setNewInputSourceType('external');
    setNewInputSourceMissionId('');
    setNewInputSourceDeliverableId('');
  });

  const getEffectiveDepIds = (t) => {
    const ids = getAllConditionTaskIds(t.unlockConditions);
    if (ids.length > 0) return ids;
    const deps = Array.isArray(t.dependsOn) ? t.dependsOn : [];
    return deps.map(dep => {
      if (typeof dep === 'string') return dep;
      return dep?.nodeId || dep?.taskId || null;
    }).filter(Boolean);
  };

  // Get upstream tasks (tasks reachable by tracing dependencies backwards)
  const upstreamTasks = useMemo(() => {
    const visited = new Set();
    const upstream = [];
    
    const queue = [...getEffectiveDepIds(task)];
    
    while (queue.length > 0) {
      const depId = queue.shift();
      if (visited.has(depId)) continue;
      visited.add(depId);
      
      const depTask = allTasks.find(t => t.id === depId);
      if (!depTask) continue;
      
      if (depTask.nodeType !== 'decision') {
        upstream.push(depTask);
      }
      
      const nextDeps = getEffectiveDepIds(depTask);
      queue.push(...nextDeps);
    }
    
    return upstream;
  }, [allTasks, task.dependsOn, task.unlockConditions]);

  // Get available deliverables from upstream tasks
  const availableUpstreamDeliverables = useMemo(() => {
    return upstreamTasks
      .filter(t => t.deliverables && t.deliverables.length > 0)
      .map(t => ({
        mission: t,
        deliverables: t.deliverables,
      }));
  }, [upstreamTasks]);

  // Get deliverable name for display
  const getDeliverableName = (missionId, deliverableId) => {
    const mission = allTasks.find(t => t.id === missionId);
    if (!mission || !mission.deliverables) return 'Unknown';
    const deliverable = mission.deliverables.find(d => d.id === deliverableId);
    return deliverable?.name || 'Unknown';
  };

  // Get source mission name for display
  const getSourceMissionName = (missionId) => {
    if (!missionId) return 'Unknown Mission';
    const mission = allTasks.find(t => t.id === missionId);
    return mission?.name || `Mission ${missionId.slice(-4)}`;
  };

  // Deliverable handlers
  const handleAddDeliverable = () => {
    if (!newDeliverableName.trim()) return;
    
    const newDeliverable = {
      id: `del-${Date.now()}`,
      name: newDeliverableName.trim(),
      link: '',
    };
    
    const updated = [...deliverables, newDeliverable];
    setDeliverables(updated);
    onDeliverablesChange?.(task.id, updated);
    
    setNewDeliverableName('');
    setShowAddDeliverable(false);
  };

  // Direct add deliverable with full data (for modal)
  // Accepts either a string (name only) or an object { name, fileType, description }
  const addDeliverableByName = (data) => {
    if (!data) return;
    
    // Handle both string (legacy) and object formats
    const isObject = typeof data === 'object';
    const name = isObject ? data.name : data;
    
    if (!name || !name.trim()) return;
    
    const newDeliverable = {
      id: `del-${Date.now()}`,
      name: name.trim(),
      fileType: isObject ? data.fileType : undefined,
      description: isObject ? data.description : undefined,
      link: '',
    };
    
    const updated = [...deliverables, newDeliverable];
    setDeliverables(updated);
    onDeliverablesChange?.(task.id, updated);
  };

  const handleRemoveDeliverable = (id) => {
    const updated = deliverables.filter(d => d.id !== id);
    setDeliverables(updated);
    onDeliverablesChange?.(task.id, updated);
  };

  // Update an existing deliverable (for editing)
  const handleUpdateDeliverable = (id, data) => {
    const updated = deliverables.map(d => 
      d.id === id ? { ...d, ...data } : d
    );
    setDeliverables(updated);
    onDeliverablesChange?.(task.id, updated);
  };

  const handleDeliverableLinkChange = (id, link) => {
    const updated = deliverables.map(d => 
      d.id === id ? { ...d, link } : d
    );
    setDeliverables(updated);
    onDeliverablesChange?.(task.id, updated);
  };

  // Input handlers
  const handleAddInput = () => {
    if (newInputSourceType === 'external') {
      if (!newInputName.trim()) return;
      
      const newInput = {
        id: `inp-${Date.now()}`,
        name: newInputName.trim(),
        sourceType: 'external',
      };
      
      const updated = [...inputs, newInput];
      setInputs(updated);
      onInputsChange?.(task.id, updated);
    } else {
      if (!newInputSourceMissionId || !newInputSourceDeliverableId) return;
      
      const deliverableName = getDeliverableName(newInputSourceMissionId, newInputSourceDeliverableId);
      
      const newInput = {
        id: `inp-${Date.now()}`,
        name: deliverableName,
        sourceType: 'mission',
        sourceMissionId: newInputSourceMissionId,
        sourceDeliverableId: newInputSourceDeliverableId,
      };
      
      const updated = [...inputs, newInput];
      setInputs(updated);
      onInputsChange?.(task.id, updated);
    }
    
    // Reset form
    setNewInputName('');
    setNewInputSourceType('external');
    setNewInputSourceMissionId('');
    setNewInputSourceDeliverableId('');
    setShowAddInput(false);
  };

  const handleRemoveInput = (id) => {
    const updated = inputs.filter(i => i.id !== id);
    setInputs(updated);
    onInputsChange?.(task.id, updated);
  };

  return {
    // Deliverables state
    deliverables,
    showAddDeliverable,
    setShowAddDeliverable,
    newDeliverableName,
    setNewDeliverableName,
    
    // Inputs state
    inputs,
    showAddInput,
    setShowAddInput,
    newInputName,
    setNewInputName,
    newInputSourceType,
    setNewInputSourceType,
    newInputSourceMissionId,
    setNewInputSourceMissionId,
    newInputSourceDeliverableId,
    setNewInputSourceDeliverableId,
    
    // Computed
    availableUpstreamDeliverables,
    
    // Handlers
    handleAddDeliverable,
    addDeliverableByName,
    handleRemoveDeliverable,
    handleUpdateDeliverable,
    handleDeliverableLinkChange,
    handleAddInput,
    handleRemoveInput,
    getSourceMissionName,
  };
};

export default useArtifacts;

